// Activities Grid — Client Component
// Fetches active activities from API and renders the grid
// Separated from page.tsx so page.tsx can be a Server Component (metadata, canonical)

'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useActivities, type Activity } from '@/lib/hooks/useActivities'
import { getAdventureHeroImages } from '@/lib/data/adventures'
import styles from './page.module.css'

// Fallback hero images from static adventures data
const heroImageFallbacks = getAdventureHeroImages()

export default function BookingActivitiesGrid() {
  const t = useTranslations('bookingActivities')
  const { activities, loading, error } = useActivities()

  const categoryLabels: Record<string, string> = {
    heliski: t('categories.heliski'),
    skiing: t('categories.skiing'),
    fishing: t('categories.fishing'),
    water: t('categories.water'),
  }

  const difficultyLabels: Record<string, string> = {
    easy: t('difficulties.easy'),
    moderate: t('difficulties.moderate'),
    challenging: t('difficulties.challenging'),
    expert: t('difficulties.expert'),
  }

  // Loading state
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <header className={styles.header} data-hero-background>
            <nav className={styles.breadcrumb}>
              <Link href="/booking">{t('breadcrumb.booking')}</Link>
              <span>/</span>
              <span>{t('breadcrumb.activities')}</span>
            </nav>
            <span className={styles.eyebrow}>{t('eyebrow')}</span>
            <h1 className={styles.title}>
              {t('pageTitle')} <span className={styles.titleAccent}>{t('pageTitleAccent')}</span>
            </h1>
          </header>

          {/* Skeleton Grid */}
          <div className={styles.activitiesGrid}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={styles.activityCardSkeleton}>
                <div className={`${styles.skeleton} ${styles.skeletonNumber}`} />
                <div className={styles.skeletonCardInfo}>
                  <div className={`${styles.skeleton} ${styles.skeletonCardName}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonCardDesc}`} />
                  <div className={styles.skeletonMeta}>
                    <div className={`${styles.skeleton} ${styles.skeletonTag}`} />
                    <div className={`${styles.skeleton} ${styles.skeletonTag}`} />
                    <div className={`${styles.skeleton} ${styles.skeletonTag}`} />
                  </div>
                  <div className={styles.skeletonFooter}>
                    <div className={styles.skeletonPrice}>
                      <div className={`${styles.skeleton} ${styles.skeletonPriceLabel}`} />
                      <div className={`${styles.skeleton} ${styles.skeletonPriceValue}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <header className={styles.header} data-hero-background>
            <nav className={styles.breadcrumb}>
              <Link href="/booking">{t('breadcrumb.booking')}</Link>
              <span>/</span>
              <span>{t('breadcrumb.activities')}</span>
            </nav>
            <span className={styles.eyebrow}>{t('eyebrow')}</span>
            <h1 className={styles.title}>
              {t('pageTitle')} <span className={styles.titleAccent}>{t('pageTitleAccent')}</span>
            </h1>
          </header>
          <div className={styles.errorState}>
            <p>{t('error.loadFailed')}: {error}</p>
            <button onClick={() => window.location.reload()}>{t('error.tryAgain')}</button>
          </div>
        </div>
      </div>
    )
  }

  // Transport/services have no public pages — exclude them from this grid.
  // They are surfaced only as upsells via ServiceRecommendation in lodge/activity flows.
  //
  // Defensive: lowercase + check both 'transport' (current convention) and 'service'
  // (legacy/future-proof). Also exclude by id as a final safety net — if the doc's
  // category field is missing or mistyped, a service like `ground-transportation`
  // would otherwise leak into the public listing.
  const SERVICE_CATEGORIES = new Set(['transport', 'service'])
  const SERVICE_IDS = new Set(['ground-transportation'])
  const nonTransportActivities = activities.filter((a) => {
    const cat = (a.category ?? '').toLowerCase()
    if (SERVICE_CATEGORIES.has(cat)) return false
    if (SERVICE_IDS.has(a.id)) return false
    return true
  })

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header} data-hero-background>
          <nav className={styles.breadcrumb}>
            <Link href="/booking">{t('breadcrumb.booking')}</Link>
            <span>/</span>
            <span>{t('breadcrumb.activities')}</span>
          </nav>
          <span className={styles.eyebrow}>{t('eyebrow')}</span>
          <h1 className={styles.title}>
            {t('pageTitle')} <span className={styles.titleAccent}>{t('pageTitleAccent')}</span>
          </h1>
          <p className={styles.description}>
            {t('subtitle')}
          </p>
        </header>

        {/* Activities Grid — transport (services) excluded, no public pages */}
        <ActivityCardGrid activities={nonTransportActivities} startIndex={0} categoryLabels={categoryLabels} difficultyLabels={difficultyLabels} t={t} />
      </div>
    </div>
  )
}

// ─── Internal helpers ────────────────────────────────────────────────────────

interface ActivityCardGridProps {
  activities: Activity[]
  startIndex: number
  categoryLabels: Record<string, string>
  difficultyLabels: Record<string, string>
  t: (key: string) => string
}

function ActivityCardGrid({ activities, startIndex, categoryLabels, difficultyLabels, t }: ActivityCardGridProps) {
  return (
    <div className={styles.activitiesGrid}>
      {activities.map((activity, idx) => (
        <Link
          key={activity.id}
          href={`/booking/activities/${activity.id}`}
          className={styles.activityCard}
          data-category={activity.category}
        >
          <div
            className={styles.cardImage}
            style={{
              backgroundImage: `url(${activity.heroImage || heroImageFallbacks[activity.id] || '/images/placeholder-activity.jpg'})`,
              backgroundPosition: activity.focalPoint
                ? `${activity.focalPoint.x}% ${activity.focalPoint.y}%`
                : undefined,
            }}
          />
          <div className={styles.cardOverlay} />

          <div className={styles.cardContent}>
            <span className={styles.cardNumber}>{String(startIndex + idx + 1).padStart(2, '0')}</span>

            <div className={styles.cardInfo}>
              <h2 className={styles.cardName}>{activity.name}</h2>
              <p className={styles.cardDescription}>{activity.shortDescription || activity.description || ''}</p>

              <div className={styles.cardMeta}>
                {activity.category && (
                  <span className={styles.metaTag}>
                    {categoryLabels[activity.category] || activity.category}
                  </span>
                )}
                {activity.difficulty && (
                  <span className={styles.metaTag}>
                    {difficultyLabels[activity.difficulty]}
                  </span>
                )}
                {activity.durationHours && (
                  <span className={styles.metaTag}>
                    {activity.durationHours}h
                  </span>
                )}
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.cardPrice}>
                  <span className={styles.priceLabel}>{t('priceFrom')}</span>
                  <span className={styles.priceValue}>
                    ${activity.basePrice?.amount?.toLocaleString() || 'N/A'}
                  </span>
                  <span className={styles.priceUnit}>
                    {activity.category === 'transport' ? t('perVehicle') : t('perPerson')}
                  </span>
                </div>

                <div className={styles.cardCta}>
                  <span className={styles.ctaText}>{t('bookNow')}</span>
                  <div className={styles.ctaArrow}>
                    <ArrowIcon className={styles.ctaIcon} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
